import { cn } from '@/lib/utils';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, CalculatorIcon, CompassIcon, StarIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { MaterialCatalogCard } from './MaterialCatalogCard';
import { categories, type Product } from '../data/products';
import { getProjects } from '../data/projects';
type CatalogFilter = 'popular' | 'all' | string;
type CatalogProduct = Product & {
  categoryName: string;
};
const TOP_SELLER_SLUGS = new Set(['5-8-minus', '1-25-minus', '5-8-clear', '1-5-drain-rock', '3-way-topsoil', 'medium-bark']);
export function Services() {
  const [searchParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState<CatalogFilter>('popular');
  // Deduped in case the data file carries locale duplicates of a slug.
  const projectGuides = useMemo(() => Array.from(new Map(getProjects().map(project => [project.slug, project])).values()), []);
  const filterRailRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const requestedCategory = searchParams.get('category');
    setSelectedFilter(requestedCategory && categories.some(category => category.name === requestedCategory) ? requestedCategory : 'popular');
  }, [searchParams]);
  const catalogProducts = useMemo<CatalogProduct[]>(() => categories.flatMap(category => category.products.map(product => ({
    ...product,
    categoryName: category.name
  }))), []);
  const visibleProducts = useMemo(() => {
    if (selectedFilter === 'popular') {
      return catalogProducts.filter(product => TOP_SELLER_SLUGS.has(product.slug));
    }
    if (selectedFilter === 'all') {
      return catalogProducts;
    }
    return catalogProducts.filter(product => product.categoryName === selectedFilter);
  }, [catalogProducts, selectedFilter]);
  const groupedCatalog = useMemo(() => categories.map(category => ({
    category,
    products: catalogProducts.filter(product => product.categoryName === category.name)
  })).filter(group => group.products.length > 0), [catalogProducts]);
  const activeCategory = useMemo(() => selectedFilter === 'popular' || selectedFilter === 'all' ? null : categories.find(category => category.name === selectedFilter) ?? null, [selectedFilter]);
  const filters = useMemo(() => [{
    id: 'popular',
    label: 'Most Popular',
    count: catalogProducts.filter(product => TOP_SELLER_SLUGS.has(product.slug)).length
  }, {
    id: 'all',
    label: 'All Materials',
    count: catalogProducts.length
  }, ...categories.map(category => ({
    id: category.name,
    label: category.name,
    count: category.products.length
  }))], [catalogProducts]);
  const selectFilter = (filterId: CatalogFilter) => {
    setSelectedFilter(filterId);
    window.requestAnimationFrame(() => {
      const rail = filterRailRef.current;
      const selectedChip = rail?.querySelector<HTMLElement>(`[data-filter-id="${filterId}"]`);
      if (!rail || !selectedChip) return;
      const railBounds = rail.getBoundingClientRect();
      const chipBounds = selectedChip.getBoundingClientRect();
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      const isPartiallyHidden = chipBounds.right > railBounds.right || chipBounds.left < railBounds.left;
      const isMobile = window.matchMedia('(max-width: 767px)').matches;

      // On mobile, a tap on a cut-off category pins that exact pill to the
      // left edge. The rail itself remains natively swipeable both ways.
      if (isMobile && isPartiallyHidden) {
        rail.scrollTo({
          left: selectedChip.offsetLeft,
          behavior
        });
      } else if (chipBounds.right > railBounds.right) {
        rail.scrollBy({
          left: chipBounds.right - railBounds.right + 12,
          behavior
        });
      } else if (chipBounds.left < railBounds.left) {
        rail.scrollBy({
          left: chipBounds.left - railBounds.left - 12,
          behavior
        });
      }
    });
  };
  return <section className="relative overflow-hidden bg-[#F2EFE8] py-20 transition-colors duration-300 dark:bg-[#202020] md:py-24" id="materials" data-id="element-1599" data-mp="3urz7lscxy2dt">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#e8e0d2]/20 to-transparent dark:from-[#404040]/20" data-id="element-1600" data-mp="dkdkxf0mc3xz" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8" data-id="element-1601" data-mp="mj8nmh4gevwj">
        <div className="mb-10 md:mb-12" data-id="element-1602" data-mp="hknokl0xui9o">
          <motion.div initial={{
          width: 0,
          opacity: 0
        }} whileInView={{
          width: 64,
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1]
        }} className="mb-7 h-1 rounded-full bg-brand-orange" data-id="element-1603" data-mp="hjmdwhnu3xsh" />
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-baseline md:gap-7" data-id="element-1604" data-mp="15ai3r98qcix3">
            <motion.h2 initial={{
            opacity: 0,
            y: 18
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 20
          }} className="font-heading text-5xl font-extrabold leading-[1.1] tracking-tight text-brand-charcoal dark:text-white md:text-6xl lg:text-7xl" data-id="element-1605" data-mp="11ahndj22lxne">
              Materials <span className="font-black text-[#04A05C]" data-id="element-1606" data-mp="1167cc4bxfc7k">Hub</span>
            </motion.h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-1607" data-mp="15xpcwra1fas0">— Browse by material</span>
          </div>
          <p className="max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-1608" data-mp="s7c82xsg0c6w">
            Start with the materials customers order most, then browse by category. Every item opens a full product page with specs, delivery details, and ordering.
          </p>
        </div>

        {/* Shop by project — a browsing aid, so it lives here with the other
            browsing tools rather than competing with the estimator in the hero.
            Deliberately NOT pills: sitting directly above the category filter
            rail, a second pill row read as one long confusing 15-item control.
            A darker inset band with plain text links separates the two ideas by
            surface instead of repeating the same shape. */}
        <div className="mb-10 bg-[#E8E4DB] px-5 py-6 dark:bg-white/[0.04] sm:px-6 sm:py-7" data-id="element-1609" data-mp="37tokvfjux6us">
          <p className="font-heading text-2xl font-extrabold tracking-tight text-stone-900 dark:text-white sm:text-3xl" data-id="element-1610" data-mp="33u60ckvy6xsh">
            Have a project in mind?
          </p>
          <p className="mt-1.5 font-sub text-base font-medium text-stone-700 dark:text-white/70 sm:text-lg" data-id="element-1611" data-mp="2tv6zvplc68gk">
            Start from the job and we'll show the exact materials it needs.
          </p>
          {/* Plain text links with hairline spacers between them. Pills here
              read as a second filter control stacked above the real category
              rail; unstyled links read as content, which is what these are. */}
          <div className="mt-5 -mx-5 flex items-center overflow-x-auto scroll-smooth scrollbar-hide px-5 touch-pan-x [-webkit-overflow-scrolling:touch] sm:-mx-6 sm:px-6" data-id="element-1612" data-mp="1dbpo2qiu0966">
            {projectGuides.map((project, projectIndex) => {
            const ProjectIcon = project.icon;
            return <React.Fragment key={project.slug} data-id="element-1613" data-mp="1r346ow8tu8cp" data-mpi={projectIndex}>
                  {projectIndex > 0 && <span aria-hidden="true" className="mx-4 h-4 w-px shrink-0 bg-stone-600/50 dark:bg-white/20" data-id="element-1614" data-mp="1w15q5ia609op" />}
                  <Link to={`/projects/${project.slug}?open=1`} onClick={e => {
                // A partially-visible pill (the last one on the rail) should
                // pull itself fully into view when tapped rather than firing a
                // navigation the customer can't see the target of.
                const el = e.currentTarget;
                const rail = el.parentElement;
                if (rail) {
                  const clipped = el.offsetLeft + el.offsetWidth > rail.scrollLeft + rail.clientWidth || el.offsetLeft < rail.scrollLeft;
                  if (clipped) {
                    e.preventDefault();
                    rail.scrollTo({
                      left: el.offsetLeft - 20,
                      behavior: 'smooth'
                    });
                  }
                }
              }} className="group inline-flex shrink-0 items-center gap-2 whitespace-nowrap py-1 text-[15px] font-[500] text-[#006239] transition-colors hover:text-[#04A05C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 focus-visible:ring-offset-[#E8E4DB] dark:text-white/85 dark:hover:text-[#22c95c] dark:focus-visible:ring-offset-[#1d1e1d] sm:text-base" data-id="element-1615" data-mp="20zz75konuitm">
                    <ProjectIcon className="h-4 w-4 shrink-0 text-[#05894e] dark:text-[#4ed491]" aria-hidden="true" data-id="element-1616" data-mp="3ezkdjbo95tco" />
                    {project.name}
                  </Link>
                </React.Fragment>;
          })}
          </div>
        </div>

        <div className="mb-8" role="tablist" aria-label="Material categories" data-id="element-1617" data-mp="3igq0jl8jx31o">
          <div ref={filterRailRef} className="flex gap-2 overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide touch-pan-x [-webkit-overflow-scrolling:touch]" data-id="element-1618" data-mp="3nhvxjv98vch0">
            {filters.map(filter => {
            const isActive = selectedFilter === filter.id;
            return <button key={filter.id} data-filter-id={filter.id} type="button" role="tab" aria-selected={isActive} onClick={() => selectFilter(filter.id)} className={cn(`snap-start shrink-0 whitespace-nowrap rounded-full border px-4 py-2.5 text-sm font-bold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#04A05C] focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#131413] ${isActive ? 'border-[#04A05C] bg-[#04A05C] text-white dark:border-[#04A05C] dark:bg-[#04A05C] dark:text-[#102016]' : 'border-stone-200 bg-white/70 text-stone-800 hover:border-[#04A05C]/60 hover:bg-white dark:border-white/15 dark:bg-white/[0.06] dark:text-white/80 dark:hover:border-[#22c95c]/70 dark:hover:bg-white/[0.12]'}`, "text-[13px] font-[500]")} data-id="element-1619" data-mp="52v1qnombwa7">
                {filter.id === 'popular' && <StarIcon className="mr-1.5 inline-block h-3.5 w-3.5 -mt-0.5 fill-current" aria-hidden="true" data-id="element-1620" data-mp="14u61j4vsf34" />}
                {filter.label}
                <span className={`ml-1.5 text-xs ${isActive ? 'text-white/70 dark:text-[#102016]/65' : 'text-stone-500 dark:text-white/50'}`} data-id="element-1621" data-mp="658hzc9ls96f">{filter.count}</span>
              </button>;
          })}
          </div>
        </div>

        {selectedFilter === 'all' ? <div className="space-y-12" role="tabpanel" aria-label="All materials by category" data-id="element-1622" data-mp="3ejqld0oln0jp">
            {groupedCatalog.map(group => <section key={group.category.name} aria-labelledby={`category-${group.category.name}`} className="border-t border-stone-300/70 pt-8 first:border-t-0 first:pt-0 dark:border-white/15" data-id="element-1623" data-mp="3jijxops5glia">
                <div className="mb-5 flex items-baseline justify-between gap-4" data-id="element-1624" data-mp="39jcli5x5iwwr">
                  <div data-id="element-1625" data-mp="2pz3yseo84wma">
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#05894e] dark:text-[#4ed491]" data-id="element-1626" data-mp="2pyko9j0wlaia">Material category</p>
                    <h3 id={`category-${group.category.name}`} className="mt-1 font-heading text-2xl font-bold text-stone-900 dark:text-white" data-id="element-1627" data-mp="2zvyw4u8rgqyz">{group.category.name}</h3>
                  </div>
                  <span className="text-sm font-semibold text-stone-500 dark:text-white/50" data-id="element-1628" data-mp="38wx0jdn430i3">{group.products.length} {group.products.length === 1 ? 'item' : 'items'}</span>
                </div>
                <motion.div layout className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 sm:gap-[26px] md:grid-cols-3" data-id="element-1629" data-mp="34tiikhfm4a20">
                  {group.products.map((product, index) => <MaterialCatalogCard key={product.slug} product={product} categoryName={group.category.name} isBestSeller={TOP_SELLER_SLUGS.has(product.slug)} index={index} data-id="element-1630" data-mp="3doycszka4r84" data-mpi={index} />)}
                </motion.div>
              </section>)}
          </div> : <>
            {activeCategory && <motion.div key={activeCategory.name} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.22
        }} className="mb-7 border-l-4 border-[#04A05C] pl-5 sm:mb-8 sm:pl-6" data-id="element-1631" data-mp="3upso08phi4w0">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-1632" data-mp="d91d83aio09k">
                  <h3 className="font-heading text-3xl font-extrabold tracking-tight text-stone-900 dark:text-white/80 sm:text-4xl" data-id="element-1633" data-mp="i7w31p6wip36">{activeCategory.name}</h3>
                  <span className="text-sm font-semibold text-stone-500 dark:text-white/50" data-id="element-1634" data-mp="m7ehq384q3xk">{visibleProducts.length} {visibleProducts.length === 1 ? 'material' : 'materials'}</span>
                </div>
                <p className="mt-2 max-w-3xl font-sans text-base font-medium leading-relaxed text-stone-700 dark:text-white/75 sm:text-lg" data-id="element-1635" data-mp="w5azvezhhz56">{activeCategory.description}</p>
              </motion.div>}
            <motion.div layout className="grid grid-cols-1 gap-[22px] sm:grid-cols-2 sm:gap-[26px] md:grid-cols-3" role="tabpanel" aria-label={`${filters.find(filter => filter.id === selectedFilter)?.label ?? 'Materials'} materials`} data-id="element-1636" data-mp="2xz0fpx8slh4s">
              {visibleProducts.map((product, index) => <MaterialCatalogCard key={product.slug} product={product} categoryName={product.categoryName} isBestSeller={TOP_SELLER_SLUGS.has(product.slug)} index={index} data-id="element-1637" data-mp="2gq7q763yk2ok" data-mpi={index} />)}
            </motion.div>
          </>}

        {visibleProducts.length === 0 && selectedFilter !== 'all' && <div className="rounded-2xl border border-dashed border-stone-300 px-6 py-12 text-center text-stone-600 dark:border-white/15 dark:text-white/65" data-id="element-1638" data-mp="34eqxpjf6obfl">
            No materials are available in this category yet.
          </div>}

        <div className="mt-10 grid gap-4 md:grid-cols-2" data-id="element-1639" data-mp="2ugb3v8oruo2e">
          <Link to="/calculator" className="group flex items-center gap-4 rounded-2xl border border-stone-200 bg-white/55 p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/[0.08]" data-id="element-1640" data-mp="1o1a8p57km9wv">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#04A05C]/10 text-[#05894e] dark:bg-[#04A05C]/15 dark:text-[#4ed491]" data-id="element-1641" data-mp="37ts5l6zf5jio"><CalculatorIcon className="h-5 w-5" data-id="element-1642" data-mp="2xygs0rzqyici" /></span>
            <span className="min-w-0 flex-1" data-id="element-1643" data-mp="2xvukgscuw2ua"><span className="block font-bold text-stone-900 dark:text-white" data-id="element-1644" data-mp="32w8q63qzj0p5">Not sure how much to order?</span><span className="mt-0.5 block text-sm text-stone-600 dark:text-white/60" data-id="element-1645" data-mp="3qltg56lk2y7x">Calculate your project in yards.</span></span>
            <ArrowRight className="h-5 w-5 shrink-0 text-stone-400 transition-transform group-hover:translate-x-1 group-hover:text-[#05894e] dark:text-white/40" data-id="element-1646" data-mp="3vk4kj1t30gir" />
          </Link>
          <Link to="/help" className="group flex items-center gap-4 rounded-2xl border border-stone-200 bg-white/55 p-5 transition-colors hover:border-[#04A05C]/50 hover:bg-white dark:border-white/10 dark:bg-white/[0.05] dark:hover:bg-white/[0.08]" data-id="element-1647" data-mp="3lmgp09nnj4zj">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#04A05C]/10 text-[#05894e] dark:bg-[#04A05C]/15 dark:text-[#4ed491]" data-id="element-1648" data-mp="2a5qggjvra12r"><CompassIcon className="h-5 w-5" data-id="element-1649" data-mp="nabv3k0odoz6" /></span>
            <span className="min-w-0 flex-1" data-id="element-1650" data-mp="11b63i2nq3pxu"><span className="block font-bold text-stone-900 dark:text-white" data-id="element-1651" data-mp="11bfx007z8f8a">Need help choosing?</span><span className="mt-0.5 block text-sm text-stone-600 dark:text-white/60" data-id="element-1652" data-mp="x91ejh87zhhj">Answer a few questions for a recommendation.</span></span>
            <ArrowRight className="h-5 w-5 shrink-0 text-stone-400 transition-transform group-hover:translate-x-1 group-hover:text-[#05894e] dark:text-white/40" data-id="element-1653" data-mp="9ki713wzv8qi" />
          </Link>
        </div>

        <div className="mt-10 flex flex-col items-center justify-between gap-5 rounded-2xl border border-stone-200/70 px-6 py-5 text-center sm:flex-row sm:text-left dark:border-white/0" data-id="element-1654" data-mp="idv5smhqp4ki">
          <div data-id="element-1655" data-mp="3m02ilynvxmhw">
            <p className="font-sub text-lg text-stone-800 dark:text-white" data-id="element-1656" data-mp="3lzke6er2k1yw">Can’t find what you’re looking for?</p>
            <p className="mt-1 text-sm text-stone-600 dark:text-white/60" data-id="element-1657" data-mp="3hxpd9bd1vqln">We source materials beyond this catalog—tell us what you need.</p>
          </div>
          <Link to="/contact" className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand-button px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-buttonHover" data-id="element-1658" data-mp="1px77tfzxk10w">
            Contact Us <ArrowRight className="h-4 w-4" data-id="element-1659" data-mp="1zmqruozdq0xc" />
          </Link>
        </div>
      </div>
    </section>;
}